import React, { useEffect, useMemo, useState } from 'react';
import ReactPlayer from 'react-player';
import { mediaUrl } from './programConfig';
import ReadLessonReader from './ReadLessonReader';

function sectionByName(sections, name) {
    return (sections || []).find((s) => s.name === name) || null;
}

const STEPS = [
    { key: 'Watch', label: 'Watch' },
    { key: 'Read', label: 'Read' },
    { key: 'Do', label: 'Do' },
];

const ModulePlayer = ({
    moduleMeta,
    detail,
    loading,
    errorMsg,
    busy,
    moduleNumber = 1,
    modulesTotal = 1,
    programTitle = 'E-Learning',
    onBack,
    onBackToDashboard,
    onComplete,
    questions,
    onSaveCustomize,
}) => {
    const sections = detail?.sections || [];
    const availableSteps = useMemo(() => {
        return STEPS.filter((step) => {
            const s = sectionByName(sections, step.key);
            if (!s) return false;
            if (step.key === 'Do' && !s.contentText && !s.pdfUrl) return false;
            if (step.key === 'Watch' && !s.videoUrl && !s.contentText)
                return false;
            return true;
        });
    }, [sections]);

    const [stepIndex, setStepIndex] = useState(0);
    const [completedKeys, setCompletedKeys] = useState(() => new Set());
    const [answers, setAnswers] = useState({});
    const [qError, setQError] = useState('');
    const [videoStarted, setVideoStarted] = useState(false);
    const [videoPlaying, setVideoPlaying] = useState(false);
    const showQuestions = Array.isArray(questions) && questions.length > 0;

    useEffect(() => {
        setStepIndex(0);
        setCompletedKeys(new Set());
        setAnswers({});
        setQError('');
        setVideoStarted(false);
        setVideoPlaying(false);
    }, [detail?.id, moduleMeta?.id]);

    const activeStep = availableSteps[stepIndex] || availableSteps[0];
    const section = activeStep
        ? sectionByName(sections, activeStep.key)
        : null;
    const isLast = stepIndex >= availableSteps.length - 1;
    const totalSteps = availableSteps.length || 1;
    const progressPct = Math.round(((stepIndex + 1) / totalSteps) * 100);
    const nextStep = !isLast ? availableSteps[stepIndex + 1] : null;

    const watchSection = sectionByName(sections, 'Watch');
    const watchHasVideo = Boolean(watchSection?.videoUrl);
    const nextDisabled = busy;

    const markComplete = (key) => {
        setCompletedKeys((prev) => {
            if (prev.has(key)) return prev;
            const next = new Set(prev);
            next.add(key);
            return next;
        });
    };

    const goToRead = () => {
        markComplete('Watch');
        const readIdx = availableSteps.findIndex((s) => s.key === 'Read');
        if (readIdx >= 0) {
            setStepIndex(readIdx);
            return;
        }
        if (!isLast) setStepIndex((i) => i + 1);
    };

    const goToStep = (index) => {
        if (index < 0 || index >= availableSteps.length) return;
        const target = availableSteps[index];
        const allowed =
            index <= stepIndex ||
            completedKeys.has(target.key) ||
            (index > 0 &&
                completedKeys.has(availableSteps[index - 1].key)) ||
            index === 0;
        if (!allowed && index > stepIndex) return;
        setStepIndex(index);
    };

    const handleVideoEnded = () => {
        goToRead();
    };

    const handleContinue = () => {
        if (activeStep?.key === 'Watch') {
            goToRead();
            return;
        }
        if (activeStep) markComplete(activeStep.key);
        if (!isLast) setStepIndex((i) => i + 1);
    };

    const handleCustomizeSave = () => {
        if (questions.some((q) => !answers[q.id])) {
            setQError('Please answer all questions.');
            return;
        }
        setQError('');
        onSaveCustomize(
            questions.map((q) => ({
                questionId: q.id,
                OptionId: answers[q.id],
            }))
        );
    };

    if (loading) {
        return <p className="dv2-el__status">Loading module…</p>;
    }
    if (errorMsg) {
        return (
            <div>
                <p className="dv2-el__error">{errorMsg}</p>
                <button
                    type="button"
                    className="dv2-el__btn-ghost"
                    onClick={onBack}
                >
                    ← Back to modules
                </button>
            </div>
        );
    }

    if (showQuestions) {
        return (
            <div className="dv2-el__customize">
                <h3>Quick Question</h3>
                <p className="dv2-el__muted">
                    A few answers help personalize this module.
                </p>
                {questions.map((q) => (
                    <fieldset key={q.id} className="dv2-el__q">
                        <legend>{q.text}</legend>
                        {(q.options || []).map((opt) => (
                            <label key={opt.id} className="dv2-el__q-opt">
                                <input
                                    type="radio"
                                    name={`q-${q.id}`}
                                    checked={answers[q.id] === opt.id}
                                    onChange={() =>
                                        setAnswers((prev) => ({
                                            ...prev,
                                            [q.id]: opt.id,
                                        }))
                                    }
                                />
                                <span>{opt.text}</span>
                            </label>
                        ))}
                    </fieldset>
                ))}
                {qError ? <p className="dv2-el__error">{qError}</p> : null}
                <div className="dv2-el__player-actions">
                    <button
                        type="button"
                        className="dv2-el__btn-ghost"
                        onClick={onBack}
                        disabled={busy}
                    >
                        ← Back to modules
                    </button>
                    <button
                        type="button"
                        className="dv2-el__btn-solid"
                        onClick={handleCustomizeSave}
                        disabled={busy}
                    >
                        {busy ? 'Saving…' : 'Continue'}
                    </button>
                </div>
            </div>
        );
    }

    const videoSrc = section?.videoUrl ? mediaUrl(section.videoUrl) : '';
    const pdfHref = section?.pdfUrl ? mediaUrl(section.pdfUrl) : null;
    const isReadStep = activeStep?.key === 'Read';
    const primaryLabel = isLast
        ? busy
            ? 'Completing…'
            : 'Complete Module'
        : `Continue to ${nextStep?.label || 'Next'} →`;

    return (
        <div className="dv2-el__player">
            <div className="dv2-el__player-top">
                <div className="dv2-el__player-main">
                    <nav className="dv2-el__crumb" aria-label="Breadcrumb">
                        <button type="button" onClick={onBackToDashboard}>
                            Dashboard
                        </button>
                        <span>/</span>
                        <button type="button" onClick={onBack}>
                            E-Learning
                        </button>
                        <span>/</span>
                        <button type="button" onClick={onBack}>
                            {programTitle}
                        </button>
                        <span>/</span>
                        <span className="dv2-el__crumb-current">
                            Module {moduleNumber} of {modulesTotal}
                        </span>
                    </nav>

                    <div className="dv2-el__player-head">
                        <h3 className="dv2-el__player-title">
                            {detail?.title || moduleMeta?.title || 'Module'}
                        </h3>
                    </div>

                    <div className="dv2-el__steps" role="tablist">
                        {availableSteps.map((step, i) => {
                            const done =
                                completedKeys.has(step.key) && i !== stepIndex;
                            const active = i === stepIndex;
                            const pending = !done && !active;
                            return (
                                <button
                                    key={step.key}
                                    type="button"
                                    role="tab"
                                    aria-selected={active}
                                    className={`dv2-el__step${
                                        active ? ' is-active' : ''
                                    }${done ? ' is-done' : ''}${
                                        pending ? ' is-pending' : ''
                                    }`}
                                    onClick={() => goToStep(i)}
                                >
                                    <span className="dv2-el__step-mark">
                                        {done ? '✓' : i + 1}
                                    </span>
                                    {step.label}
                                </button>
                            );
                        })}
                    </div>

                    <div
                        className="dv2-el__progress"
                        aria-label={`Progress ${stepIndex + 1} of ${totalSteps}`}
                    >
                        <div className="dv2-el__progress-meta">
                            <span>
                                Progress {stepIndex + 1} of {totalSteps}
                            </span>
                            <span>{progressPct}%</span>
                        </div>
                        <div className="dv2-el__progress-bar">
                            <span style={{ width: `${progressPct}%` }} />
                        </div>
                    </div>
                </div>

                <div className="dv2-el__player-aside">
                    <p className="dv2-el__program-label">PROGRAM</p>
                    <h2 className="dv2-el__aside-title">{programTitle}</h2>
                </div>
            </div>

            <div className="dv2-el__step-body">
                {activeStep?.key === 'Watch' && videoSrc ? (
                    <div
                        className={`dv2-el__video${
                            videoStarted ? ' is-playing' : ' is-idle'
                        }`}
                    >
                        <ReactPlayer
                            className="dv2-el__react-player"
                            url={videoSrc}
                            width="100%"
                            height="100%"
                            controls={videoStarted}
                            playing={videoPlaying}
                            light={!videoStarted}
                            playIcon={
                                <span
                                    className="dv2-el__video-play"
                                    aria-hidden="true"
                                >
                                    <span className="dv2-el__video-play-icon" />
                                </span>
                            }
                            onClickPreview={() => {
                                setVideoStarted(true);
                                setVideoPlaying(true);
                            }}
                            onPlay={() => {
                                setVideoStarted(true);
                                setVideoPlaying(true);
                            }}
                            onPause={() => setVideoPlaying(false)}
                            onEnded={handleVideoEnded}
                            progressInterval={800}
                            onProgress={({ played }) => {
                                if (videoStarted && played >= 0.995) {
                                    handleVideoEnded();
                                }
                            }}
                        />
                    </div>
                ) : null}

                {activeStep?.key === 'Watch' && watchHasVideo ? (
                    <p className="dv2-el__hint dv2-el__hint--watch">
                        Continue to Read when you’re ready, or when the video
                        ends.
                    </p>
                ) : null}

                {activeStep?.key === 'Watch' && section?.contentText ? (
                    <div className="dv2-el__about dv2-el__about--watch">
                        <h5>About this lesson</h5>
                        <div
                            className="dv2-el__rich"
                            dangerouslySetInnerHTML={{
                                __html: String(section.contentText).replace(
                                    /className/g,
                                    'class'
                                ),
                            }}
                        />
                    </div>
                ) : null}

                {isReadStep ? (
                    <ReadLessonReader
                        introText={section?.introText}
                        contentText={section?.contentText}
                        imageUrl={section?.imageUrl}
                        moduleTitle={
                            detail?.title || moduleMeta?.title || ''
                        }
                        onContinueToDo={handleContinue}
                    />
                ) : null}

                {activeStep?.key === 'Do' ? (
                    <div className="dv2-el__about dv2-el__about--do">
                        {(section?.contentText || section?.introText) && (
                            <div
                                className="dv2-el__rich"
                                dangerouslySetInnerHTML={{
                                    __html: String(
                                        section.contentText ||
                                            section.introText ||
                                            ''
                                    ).replace(/className/g, 'class'),
                                }}
                            />
                        )}
                        {pdfHref ? (
                            <a
                                className="dv2-el__download"
                                href={pdfHref}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                Download Workbook ↓
                            </a>
                        ) : null}
                    </div>
                ) : null}
            </div>

            <div className="dv2-el__player-actions">
                <button
                    type="button"
                    className="dv2-el__btn-ghost"
                    onClick={() => {
                        if (stepIndex === 0) onBack();
                        else setStepIndex((i) => i - 1);
                    }}
                    disabled={busy}
                >
                    {stepIndex === 0 ? '← Back to modules' : '← Back'}
                </button>
                {isReadStep ? (
                    <span className="dv2-el__footer-spacer" aria-hidden />
                ) : !isLast ? (
                    <button
                        type="button"
                        className="dv2-el__btn-solid"
                        onClick={handleContinue}
                        disabled={nextDisabled}
                    >
                        {primaryLabel}
                    </button>
                ) : (
                    <button
                        type="button"
                        className="dv2-el__btn-solid"
                        onClick={() => {
                            if (activeStep) markComplete(activeStep.key);
                            onComplete();
                        }}
                        disabled={
                            busy || detail?.displayCompleteButton === false
                        }
                    >
                        {primaryLabel}
                    </button>
                )}
            </div>
        </div>
    );
};

export default ModulePlayer;
